<template>
  <vs-popup
    title="Detalle del cierre de ventas"
    :active.sync="isOpen"
    @close="handleClose"
    class="sales-closures-details-modal"
    fullscreen
  >
    <div class="sales-closures-details-container p-6 space-y-6">
      <div v-if="closure" class="space-y-6">
        <!-- Header Section with Campaign Summary -->
        <div class="bg-gradient-to-r from-latinad to-latinad-light p-6 rounded-lg text-white">
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-2xl font-bold">Cierre #{{ getClosureDisplayNumber(closure) }}</h2>
            <vs-chip
              :color="CLOSURE_STATUS_COLORS[closure.status]"
              class="text-white text-sm"
            >
              {{ CLOSURE_STATUS_LABELS[closure.status] }}
            </vs-chip>
          </div>
          <div class="grid grid-cols-4 gap-4 text-sm">
            <div>
              <p class="opacity-75 mb-1">Período</p>
              <p class="font-semibold">{{ dateFormatter(closure.date_from) }} a {{ dateFormatter(closure.date_to) }}</p>
            </div>
            <div>
              <p class="opacity-75 mb-1">Monto Total</p>
              <p class="font-semibold">{{ stringCurrencyFormatter(closure.amount) }} {{ closure.currency }}</p>
            </div>
            <div>
              <p class="opacity-75 mb-1">Creado</p>
              <p class="font-semibold">{{ dateFormatter(closure.created_at) }}</p>
              <p v-if="closure.canceled_at" class="font-semibold">Anulado: {{ dateFormatter(closure.canceled_at) }}</p>
              <p v-else-if="closure.updated_at" class="font-semibold">Actualizado: {{ dateFormatter(closure.updated_at) }}</p>
            </div>
            <div>
              <p class="opacity-75 mb-1">Modo Comisión</p>
              <p class="font-semibold">{{ AGENCY_COMMISSION_MODES_LABELS[closure.agency_commission_mode] }}</p>
            </div>
          </div>
        </div>

        <!-- Company Information Section -->
        <div class="border-t pt-6">
          <h3 class="font-semibold mb-4 text-sm uppercase">Información de la Empresa</h3>
          <div class="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded border border-gray-200">
            <div>
              <p class="text-sm text-gray-600 mb-1">Nombre Fantasía</p>
              <p class="font-semibold">{{ getClosureFantasyName(closure) }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 mb-1">Razón Social</p>
              <p class="font-semibold">{{ getClosureLegalName(closure) }}</p>
            </div>
            <div v-if="closure.responsible_agency">
              <p class="text-sm text-gray-600 mb-1">Agencia Responsable</p>
              <p class="font-semibold">{{ closure.responsible_agency.name }}</p>
            </div>
          </div>
        </div>

        <!-- Author Section -->
        <div class="border-t pt-6">
          <h3 class="font-semibold mb-4 text-sm uppercase">Autor del Cierre</h3>
          <div class="p-4 bg-blue-50 rounded border border-blue-200">
            <div v-if="closure.author" class="space-y-2">
              <p class="font-semibold text-gray-800">{{ closure.author.first_name }} {{ closure.author.last_name }}</p>
              <p class="text-sm text-gray-600">{{ closure.author.email }}</p>
              <p v-if="closure.status === 'valid'" class="text-xs text-green-700 bg-green-100 px-2 py-1 rounded w-fit">
                ✓ Autor congelado (cierre validado)
              </p>
            </div>
            <div v-else class="text-gray-500 italic">
              <p>Sin información de autor</p>
            </div>
          </div>
        </div>

        <!-- Billing & Payment Section -->
        <div class="border-t pt-6">
          <h3 class="font-semibold mb-4 text-sm uppercase">Facturación y Pago</h3>
          <div class="grid grid-cols-4 gap-4 p-4 bg-gray-50 rounded border border-gray-200">
            <div>
              <p class="text-sm text-gray-600 mb-1">Modo Facturación</p>
              <p class="font-semibold">{{ BILLING_MODES_LABELS[closure.billing_mode] }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 mb-1">Modo Pago</p>
              <p class="font-semibold">{{ PAYMENT_MODES_LABELS[closure.payment_mode] }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 mb-1">Plazo Pago (días)</p>
              <p class="font-semibold">{{ closure.payment_term }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600 mb-1">Plazo Cobro (días)</p>
              <p class="font-semibold">{{ closure.charge_term }}</p>
            </div>
          </div>
        </div>

        <!-- Commissions & Taxes Section -->
        <div class="border-t pt-6">
          <h3 class="font-semibold mb-4 text-sm uppercase">Comisiones e Impuestos</h3>
          <div class="space-y-3">
            <div class="bg-amber-50 p-4 rounded border border-amber-200">
              <p class="font-bold text-amber-900 mb-3 text-sm">Comisión Agencia</p>
              <div class="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p class="text-gray-600 mb-1">Porcentaje</p>
                  <p class="font-semibold">{{ closure.agency_commission_percentage }}%</p>
                </div>
                <div>
                  <p class="text-gray-600 mb-1">Monto</p>
                  <p class="font-bold text-amber-900">{{ stringCurrencyFormatter(closure.agency_commission_amount) }}</p>
                </div>
                <div>
                  <p class="text-gray-600 mb-1">Moneda</p>
                  <p class="font-semibold">{{ closure.agency_commission_currency }}</p>
                </div>
              </div>
            </div>

            <div class="bg-blue-50 p-4 rounded border border-blue-200">
              <p class="font-bold text-blue-900 mb-3 text-sm">Comisión Exchange</p>
              <div class="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p class="text-gray-600 mb-1">Porcentaje</p>
                  <p class="font-semibold">{{ closure.exchange_commission_percentage }}%</p>
                </div>
                <div>
                  <p class="text-gray-600 mb-1">Monto</p>
                  <p class="font-bold text-blue-900">{{ stringCurrencyFormatter(closure.exchange_commission_amount) }}</p>
                </div>
                <div>
                  <p class="text-gray-600 mb-1">Moneda</p>
                  <p class="font-semibold">{{ closure.exchange_commission_currency }}</p>
                </div>
              </div>
            </div>

            <div class="bg-red-50 p-4 rounded border border-red-200">
              <p class="font-bold text-red-900 mb-3 text-sm">Impuesto Internacional</p>
              <div class="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p class="text-gray-600 mb-1">Porcentaje</p>
                  <p class="font-semibold">{{ closure.international_tax_percentage }}%</p>
                </div>
                <div>
                  <p class="text-gray-600 mb-1">Monto</p>
                  <p class="font-bold text-red-900">{{ stringCurrencyFormatter(closure.international_tax_amount) }}</p>
                </div>
                <div>
                  <p class="text-gray-600 mb-1">Moneda</p>
                  <p class="font-semibold">{{ closure.international_tax_currency }}</p>
                </div>
              </div>
            </div>

            <div class="bg-emerald-50 p-4 rounded border border-emerald-200">
              <p class="font-bold text-emerald-900 mb-3 text-sm">Impuestos Nacionales</p>
              <div class="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p class="text-gray-600 mb-1">Porcentaje</p>
                  <p class="font-semibold">{{ closure.national_tax_percentage }}%</p>
                </div>
                <div>
                  <p class="text-gray-600 mb-1">Monto</p>
                  <p class="font-bold text-emerald-900">{{ stringCurrencyFormatter(closure.national_tax_amount) }}</p>
                </div>
                <div>
                  <p class="text-gray-600 mb-1">Moneda</p>
                  <p class="font-semibold">{{ closure.national_tax_currency }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Calculation Base -->
        <div class="border-t pt-6">
          <div class="p-6 bg-gray-50 rounded border border-gray-200">
            <p class="text-sm text-gray-600 mb-2">Base de Cálculo</p>
            <p class="font-bold text-latinad text-lg">{{ stringCurrencyFormatter(closure.calculation_base) }} {{ closure.currency }}</p>
          </div>
        </div>

      </div>
    </div>
  </vs-popup>
</template>

<style>
/* Global styles - no scoped for higher priority */
.sales-closures-details-modal {
  z-index: 99998 !important;
}

.sales-closures-details-modal .vs-popup {
  z-index: 99998 !important;
  opacity: 1 !important;
  overflow: hidden !important;
}

.sales-closures-details-modal .vs-popup--content {
  max-height: 90vh;
  overflow-y: auto !important;
  overflow-x: hidden !important;
}

.sales-closures-details-container {
  width: 100%;
  max-width: 980px;
  margin: 0 auto;
}

.sales-closures-details-modal .vs-popup__header {
  position: relative !important;
}

.sales-closures-details-modal .vs-popup--close {
  opacity: 1 !important;
  position: absolute !important;
  top: 16px !important;
  right: 16px !important;
  z-index: 100000 !important;
  background: white !important;
  width: 40px !important;
  height: 40px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  cursor: pointer !important;
  border: 1px solid #e0e0e0 !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
}

.sales-closures-details-modal .vs-popup--close--icon {
  opacity: 1 !important;
  color: #333 !important;
  font-size: 20px !important;
  margin: 0 !important;
}
</style>

<script>
import {
  CLOSURE_STATUS_LABELS,
  CLOSURE_STATUS_COLORS,
  BILLING_MODES_LABELS,
  PAYMENT_MODES_LABELS,
  AGENCY_COMMISSION_MODES_LABELS
} from '@/constants/salesClosures'
import { dateFormatter, stringCurrencyFormatter } from '@/utils/strings'

export default {
  name: 'SalesClosuresDetails',
  props: {
    active: {
      type: Boolean,
      default: false
    },
    closure: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isOpen: false,
      // Constants for template
      CLOSURE_STATUS_LABELS,
      CLOSURE_STATUS_COLORS,
      BILLING_MODES_LABELS,
      PAYMENT_MODES_LABELS,
      AGENCY_COMMISSION_MODES_LABELS,
      dateFormatter,
      stringCurrencyFormatter
    }
  },
  watch: {
    active(val) {
      this.isOpen = val
    },
    isOpen(val) {
      this.$emit('update:active', val)
    }
  },
  mounted() {
    this.isOpen = this.active
  },
  methods: {
    getClosureFantasyName(closure) {
      return (
        (closure && closure.company_fantasy_name) ||
        (closure && closure.fantasy_name) ||
        (closure && closure.company && closure.company.fantasy_name) ||
        '-'
      )
    },

    getClosureLegalName(closure) {
      return (
        (closure && closure.company_legal_name) ||
        (closure && closure.legal_name) ||
        (closure && closure.billing_information_name) ||
        (closure && closure.billing_information && closure.billing_information.name) ||
        '-'
      )
    },

    getClosureDisplayNumber(closure) {
      return (closure && (closure.number || closure.closure_number || closure.id)) || '-'
    },

    handleClose() {
      this.$emit('close')
      this.isOpen = false
    }
  }
}
</script>
